<template>
  <div class="phone-field-wrapper">
    <div class="phone-field" :class="{ 'is-error': error }" v-click-outside="close">
      <!-- LEFT: flag + dial code + caret -->
      <button class="prefix" type="button" @click="open = !open" :disabled="disabled">
        <span class="flag" :class="`fi fi-${selected.iso2.toLowerCase()}`"></span>
        <span class="dial">+{{ selected.dialCode }}</span>
        <svg class="caret" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
          <path d="M4 6l4 4 4-4" fill="none" stroke="#252EAB" stroke-width="2" stroke-linecap="round"
            stroke-linejoin="round" />
        </svg>
      </button>

      <div class="divider" aria-hidden="true"></div>

      <!-- RIGHT: the actual input -->
      <input class="number" type="text" inputmode="numeric" :value="national" :placeholder="placeholder"
        :disabled="disabled" @input="onInput" @blur="$emit('blur')" @focus="$emit('focus')" autocomplete="tel"
        :aria-invalid="error ? 'true' : 'false'" />



      <!-- DROPDOWN -->
      <div class="dropdown" v-show="open">
        <div class="search-wrap">
          <input class="search" type="text" placeholder="Keresés" v-model="query" autocomplete="off" />
        </div>

        <ul class="menu" role="listbox">
          <li v-for="c in filtered" :key="c.iso2" class="item" :class="{ active: c.iso2 === selected.iso2 }"
            @click="pick(c)" role="option">
            <span class="flag" :class="`fi fi-${c.iso2.toLowerCase()}`"></span>
            <span class="name">{{ c.name }}</span>
            <span class="code">+{{ c.dialCode }}</span>
          </li>
        </ul>
      </div>
    </div>

    <!-- Error message (same pattern as select/input) -->
    <div v-if="error && errorMessage" class="phone-error-container">
      <img :src="errorLogo" alt="Phone error icon" class="phone-error-icon" />
      <span class="phone-error">{{ errorMessage }}</span>
    </div>
  </div>
</template>

<script>
import errorLogo from "@/assets/error-icon.svg";

/**
 * Plain-HTML phone field with country dropdown.
 * Emits:
 *  - input: full E.164 string (e.g. +36301234567) or '' if empty
 *  - change-country: the country object { iso2, name, dialCode, flag }
 * Props:
 *  - value: String  (E.164 or raw digits; component normalizes)
 *  - countries: Array of { iso2, name, dialCode, flag }
 *  - defaultIso2: String ('hu' by default)
 *  - placeholder: String
 *  - disabled: Boolean
 *  - error: Boolean (show red border)
 *  - errorMessage: String (text below field)
 */
const DEFAULT_COUNTRIES = [
  { iso2: 'hu', name: 'Hungary', dialCode: '36', flag: '🇭🇺' },
  { iso2: 'ro', name: 'Romania', dialCode: '40', flag: '🇷🇴' },
  { iso2: 'sk', name: 'Slovakia', dialCode: '421', flag: '🇸🇰' },
  { iso2: 'de', name: 'Germany', dialCode: '49', flag: '🇩🇪' },
  { iso2: 'at', name: 'Austria', dialCode: '43', flag: '🇦🇹' },
  { iso2: 'us', name: 'United States', dialCode: '1', flag: '🇺🇸' },
];

export default {
  name: 'PhoneInput',
  props: {
    value: { type: String, default: '' },
    countries: { type: Array, default: () => DEFAULT_COUNTRIES },
    defaultIso2: { type: String, default: 'hu' },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    errorMessage: { type: String, default: '' },
  },
  data() {
    const selected = this.countries.find(c => c.iso2 === this.defaultIso2) || this.countries[0];
    const parsed = this.parseInitial(this.value, selected);
    return {
      open: false,
      query: '',
      selected: parsed.country,
      national: parsed.nsn, // national significant number (digits only)
      errorLogo,
    };
  },
  directives: {
    // simple click-outside directive
    clickOutside: {
      bind(el, binding) {
        el.__clickOutside__ = (e) => {
          if (!(el === e.target || el.contains(e.target))) binding.value(e);
        };
        document.addEventListener('click', el.__clickOutside__);
      },
      unbind(el) {
        document.removeEventListener('click', el.__clickOutside__);
        delete el.__clickOutside__;
      }
    }
  },
  computed: {
    filtered() {
      const q = this.query.trim().toLowerCase();
      if (!q) return this.countries;
      return this.countries.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.iso2.toLowerCase().includes(q) ||
        ('+' + c.dialCode).includes(q.replace(/\s/g, ''))
      );
    },
    displayNumber() {
      // formatting hook; now it's just digits
      return this.national;
    }
  },
  watch: {
    value(newVal) {
      // keep in sync if parent changes v-model externally
      const parsed = this.parseInitial(newVal, this.selected);
      this.national = parsed.nsn;
      if (parsed.country.iso2 !== this.selected.iso2) {
        this.selected = parsed.country;
      }
    }
  },
  methods: {
    close() { this.open = false; },
    parseInitial(val, fallbackCountry) {
      const raw = (val || '').toString();
      // If it starts with +, try to match any country dial code prefix
      if (raw.startsWith('+')) {
        const digits = raw.replace(/[^\d]/g, '');
        // Find the longest matching dial code
        const match = this.countries
          .slice()
          .sort((a, b) => b.dialCode.length - a.dialCode.length)
          .find(c => digits.startsWith(c.dialCode));
        if (match) {
          return { country: match, nsn: digits.slice(match.dialCode.length) };
        }
        // Fallback: keep fallbackCountry
        return { country: fallbackCountry, nsn: digits };
      }
      // Otherwise assume it's just national digits for the fallback
      return { country: fallbackCountry, nsn: raw.replace(/[^\d]/g, '') };
    },
    onInput(e) {
      const raw = e.target.value || '';

      // Strip non-digits and limit to 9
      const digits = raw.replace(/\D/g, '').slice(0, 9);

      // Force the input to show only the clean value
      e.target.value = digits;

      // Update internal state
      this.national = digits;

      // Emit E.164 format
      const e164 = digits ? `+${this.selected.dialCode}${digits}` : '';
      this.$emit('input', e164);
      this.$emit('valid', digits.length === 9);
    },
    pick(c) {
      if (c.iso2 === this.selected.iso2) { this.open = false; return; }
      this.selected = c;
      this.open = false;
      const e164 = this.national ? `+${c.dialCode}${this.national}` : '';
      this.$emit('change-country', c);
      this.$emit('input', e164);
    }
  }
};
</script>

<style scoped>
.phone-field-wrapper {
  width: 326px;
}

/* main field */
.phone-field {
  position: relative;
  display: flex;
  align-items: center;
  height: 44px;
  width: 100%;
  background: #fff;
  border: 1px solid #9E9E9E;
  border-radius: 12px;
  padding: 0 12px;
  box-sizing: border-box;
}

/* error variant – match other components */
.phone-field.is-error {
  border-color: #D64040 !important;
  border-width: 2px;
  background-color: unset !important;
}

.prefix {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
}

.prefix:disabled {
  cursor: default;
  opacity: .6;
}

.flag {
  border-radius: 50%;
  width: 24px;
  height: 25px;
  display: inline-block;
  overflow: hidden;
  border: 1px solid #cccccc;
  background-size: cover;
}

.dial {
  color: #656565;
  font-size: 16px;
  font-weight: 300;
  line-height: 24px;
}

.caret {
  margin-left: 2px;
}

.divider {
  width: 1px;
  height: 24px;
  background: #E5E7EB;
  margin: 0 12px;
}

.number {
  border: 0;
  outline: none;
  width: 100%;
  height: 100%;
  font-size: 16px;
  color: #111827;
  background: transparent;
}

.number::placeholder {
  color: #9CA3AF;
}

/* focus ring */
.phone-field:focus-within {
  border-color: #9E9E9E;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, .12);
}

/* dropdown */
.dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 8px;
  right: 8px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 10px 22px rgba(0, 0, 0, .08);
  overflow: hidden;
  z-index: 20;
}

.search-wrap {
  padding: 8px;
  border-bottom: 1px solid #f1f5f9;
}

.search {
  width: 100%;
  height: 36px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 14px;
  outline: none;
}

.search:focus {
  box-shadow: 0 0 0 2px rgba(59, 130, 246, .08);
}

.menu {
  max-height: 260px;
  overflow: auto;
  list-style: none;
  margin: 0;
  padding: 4px 0;
}

.item {
  display: grid;
  grid-template-columns: 24px 1fr auto;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
}

.item:hover {
  background: #f8fafc;
}

.item.active {
  background: #eef2ff;
}

.item .name {
  color: #111827;
}

.item .code {
  color: #6b7280;
}

/* error row under the field */
.phone-error-container {
  display: flex;
  align-items: center;
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.4;
}

.phone-error-icon {
  height: 18px;
  margin-right: 6px;
}

.phone-error {
  color: #D64040;
}

input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}

/* (Optional) Other browsers */
input[type="number"] {
  appearance: textfield;
}

@media (max-width: 575px) {
  .phone-field-wrapper {
    width: 100%;
  }
}
</style>
